import { useEffect } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useSafeNavigate } from "../../../hooks/useSafeNavigate";
import { onAppResume, setPushTapHandler } from "../../../lib/platform";
import {
  addPushReceivedListener,
  clearDeliveredPushes,
  getPushPermission,
  isPushAvailable,
} from "../../../lib/push";
import { useNotificationsContext } from "../NotificationsProvider";
import { RECENT_NOTIFICATIONS_QUERY_KEY } from "./useRecentNotifications";
import { useRegisterPushToken } from "./useRegisterPushToken";

/**
 * Native push for the signed-in app. Mount ONCE, in the authenticated native
 * shell (NativeTabLayout) — being mounted is what "signed in, and able to open
 * a notification" means to the tap router in lib/platform. A no-op on web and
 * on a binary built before the push plugin.
 *
 * It never prompts: asking is PushSoftAskSheet's job, once, after the first
 * alert subscription.
 */
export const usePushNotifications = (): void => {
  const queryClient = useQueryClient();
  const navigate = useSafeNavigate();
  const { openNotification } = useNotificationsContext();
  const { mutate: registerToken } = useRegisterPushToken();

  // Already allowed: (re)register on every start and resume. Permission can be
  // revoked in iOS Settings while the app is backgrounded, hence the re-check.
  // Each open also clears the delivered pushes and the icon badge; the
  // registration it follows is the open the server counts the badge from.
  useEffect(() => {
    if (!isPushAvailable()) return undefined;
    const registerIfGranted = () => {
      void getPushPermission().then((permission) => {
        if (permission !== "granted") return;
        registerToken(undefined, { onSettled: () => void clearDeliveredPushes() });
      });
    };
    registerIfGranted();
    return onAppResume(registerIfGranted);
  }, [registerToken]);

  // A tapped push opens like everything else: through openNotification, on the
  // destination the server resolves at that moment. A summary push carries a
  // (guarded) path instead and marks nothing read.
  useEffect(
    () =>
      setPushTapHandler((tap) => {
        if (tap.kind === "notification") {
          openNotification(tap.id, "push");
          return;
        }
        const currentPath = `${window.location.pathname}${window.location.search}`;
        if (tap.path !== currentPath) navigate(tap.path);
      }),
    [openNotification, navigate],
  );

  // iOS shows no banner while the app is in the foreground; the badge and the
  // inbox's New section are how the arrival shows.
  useEffect(
    () =>
      addPushReceivedListener(() => {
        void queryClient.invalidateQueries(
          { queryKey: RECENT_NOTIFICATIONS_QUERY_KEY },
          { cancelRefetch: false },
        );
      }),
    [queryClient],
  );
};
